import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useOrderLimits } from '../context/OrderLimitsContext';
import { getApiUrl } from '../config/api';
import CheckoutModal from './CheckoutModal';
import CartSummary from './CartSummary';


const ShoppingCart: React.FC = () => {
  const { t } = useTranslation();
  const { state, priceCalculation, isCalculatingPrice, updateQuantity, removeFromCart } = useCart();
  const { config, isOrderValid, getOrderStatus } = useOrderLimits();
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  const totalArticles = state.items.reduce((total, item) => total + item.quantity, 0);
  const orderStatus = getOrderStatus(totalArticles);

  // Get credit limit info from price calculation
  const creditInfo = priceCalculation?.credit_limit;
  const currentTotal = priceCalculation?.totals.inclVat || state.total;

  const creditAfterOrder = creditInfo?.available_credit !== null && creditInfo?.available_credit !== undefined
    ? creditInfo.available_credit - currentTotal
    : null;

  // Check if credit limit is exceeded (either already or after this order)
  const creditLimitExceeded = creditInfo &&
    creditInfo.available_credit !== null &&
    (creditAfterOrder !== null && creditAfterOrder < 0);

  const checkoutEnabled = isOrderValid(totalArticles) &&
    state.items.length > 0 &&
    !creditLimitExceeded;

  const handleCheckout = () => {
    setShowCheckoutModal(true);
  };

  const handleQuantityDecrease = (vehicleId: string, currentQuantity: number) => {
    if (currentQuantity > 1) {
      updateQuantity(vehicleId, currentQuantity - 1);
    } else {
      removeFromCart(vehicleId);
    }
  };

  const handleQuantityIncrease = (vehicleId: string, currentQuantity: number) => {
    updateQuantity(vehicleId, currentQuantity + 1);
  };

  const handleQuantityChange = (vehicleId: string, value: string) => {
    const newQuantity = Math.max(1, parseInt(value) || 1);
    updateQuantity(vehicleId, newQuantity);
  };

  return (
    <>
      <Card className="sticky-top d-flex flex-column" style={{ top: '20px', height: 'calc(100vh - 140px)' }}>
        <Card.Header className="bg-dark text-white flex-shrink-0">
          <h5 className="mb-0">{t('cart.title')}</h5>
        </Card.Header>
        <Card.Body className="flex-grow-1 overflow-hidden d-flex flex-column p-0">
          <div className="flex-grow-1 overflow-auto p-3" id="cart-items">
            {state.items.length === 0 ? (
              <div className="text-center py-4">
                <p className="mb-0">{t('cart.empty')}</p>
                <small className="text-muted">{t('cart.emptyDescription')}</small>
              </div>
            ) : (
              <div className="cart-items-list">
                {state.items.map((item) => (
                  <div key={item.vehicle.id} className="cart-item border-bottom py-3">
                    <div className="d-flex flex-column gap-2">
                      {/* Item name and remove button */}
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="cart-item-title">{item.vehicle.name}</div>
                        <Button
                          variant="link"
                          className="p-0 text-muted"
                          onClick={() => removeFromCart(item.vehicle.id)}
                          title={t('cart.removeItem')}
                          style={{ fontSize: '16px', lineHeight: 1 }}
                        >
                          ×
                        </Button>
                      </div>

                      {/* Quantity controls and price */}
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-1">
                          <div className="input-group" style={{ maxWidth: '80px' }}>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="px-1"
                              onClick={() => handleQuantityDecrease(item.vehicle.id, item.quantity)}
                              style={{ fontSize: '12px' }}
                            >
                              -
                            </Button>
                            <Form.Control
                              type="number"
                              size="sm"
                              className="text-center px-1"
                              value={item.quantity}
                              min="1"
                              onChange={(e) => handleQuantityChange(item.vehicle.id, e.target.value)}
                              style={{ minWidth: '35px', fontSize: '12px' }}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="px-1"
                              onClick={() => handleQuantityIncrease(item.vehicle.id, item.quantity)}
                              style={{ fontSize: '12px' }}
                            >
                              +
                            </Button>
                          </div>
                          <small className="text-muted" style={{ fontSize: '12px' }}>
                            × €{item.vehicle.price.toFixed(2)}
                          </small>
                        </div>
                        <div className="fw-bold">
                          €{item.totalPrice.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card.Body>
        <Card.Footer className="flex-shrink-0 border-top cart-footer">
          {orderStatus.message && (
            <Alert
              variant={orderStatus.color}
              className="mb-2 py-2 px-3 cart-alert"
            >
              {orderStatus.message}
            </Alert>
          )}

          <CartSummary
            cartState={state}
            priceCalculation={priceCalculation}
            isCalculatingPrice={isCalculatingPrice}
            showArticleCount={true}
            maxArticlesPerOrder={config.maxArticlesPerOrder}
            articleCountColor={orderStatus.color}
          />

          <Button
            variant="primary"
            className="w-100 mt-3"
            id="checkout-btn"
            disabled={!checkoutEnabled}
            onClick={handleCheckout}
            title={creditLimitExceeded ? t('cart.creditLimit.exceededTooltip') : ''}
          >
            {creditLimitExceeded ? (
              <>
                <i className="fas fa-exclamation-triangle me-2"></i>
                {t('cart.creditLimit.exceeded')}
              </>
            ) : (
              t('cart.checkout')
            )}
          </Button>
        </Card.Footer>
      </Card>

      <CheckoutModal
        show={showCheckoutModal}
        onHide={() => setShowCheckoutModal(false)}
      />
    </>
  );
};

export default ShoppingCart;